import React, { Fragment, useEffect, useState } from 'react';
import { CardBody, Row, Table } from 'reactstrap';

import {
  Column,
  Table as ReactTable,
  FilterFn,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  ColumnDef,
} from '@tanstack/react-table';

import { rankItem } from '@tanstack/match-sorter-utils';

import { useSelector } from 'react-redux';
import { RootState } from 'slices';

import {
  EntriesPagination,
  ResultsPagination,
} from 'Components/pos2u/Pagination';

import TextSelectedItems from 'Components/pos2u/TextSelectedItems';
import { useTranslation } from 'react-i18next';
import TagSearchForm from 'pages/Tags/TagSearchForm';
import useWindowSize from 'Components/Hooks/WindowSizeHook';

// Column Filter
const Filter = ({
  column,
}: {
  column: Column<any, unknown>;
  table: ReactTable<any>;
}) => {
  const columnFilterValue = column.getFilterValue();

  return (
    <>
      <DebouncedInput
        type="text"
        // value={(columnFilterValue ?? '') as string}
        value={typeof columnFilterValue === 'string' ? columnFilterValue : ''}
        onChange={(value) => column.setFilterValue(value)}
        placeholder="Search..."
        className="w-36 border shadow rounded"
        list={column.id + 'list'}
      />
      <div className="h-1" />
    </>
  );
};

// Global Filter
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <input
      {...props}
      value={value}
      id="search-bar-0"
      className="form-control search"
      onChange={(e) => setValue(e.target.value)}
    />
  );
};

interface TableContainerProps {
  columns: ColumnDef<any, any>[];
  data?: any;
  isTagQuery?: boolean;
  customPageSize: number;
  tableClass?: string;
  theadClass?: string;
  trClass?: string;
  thClass?: string;
  tdClass?: string;
  divClass?: string;
  selectedCheckBoxDelete?: number;
  TotalData: number;
  totalPages: number;
  currentPage: number;
  setEntriesPerPage: (value: number) => void;
  handlePageChange: (value: number) => void;
  handleSearchFiltersSubmit?: (value: any) => void;
  initialFilters?: any;
  inputPageValue: number | null;
  setInputPageValue: (value: number | null) => void;
  // handleCreateItem: () => void;
  // isAdding: boolean;
  // newItem: TagRequest;
  // setNewItem: (value: TagRequest) => void;
  // handleCancelCreateItem: () => void;
  // inputRef?: React.RefObject<HTMLInputElement>;
}

const TagTable = ({
  columns,
  data,
  isTagQuery = false,
  customPageSize,
  tableClass,
  theadClass,
  trClass,
  thClass,
  tdClass = '',
  divClass,
  selectedCheckBoxDelete,
  TotalData,
  totalPages,
  currentPage,
  setEntriesPerPage,
  handlePageChange,
  handleSearchFiltersSubmit,
  initialFilters,
  inputPageValue,
  setInputPageValue,
}: // handleCreateItem,
// isAdding,
// newItem,
// setNewItem,
// handleCancelCreateItem,
// inputRef,
TableContainerProps) => {
  const layoutState = useSelector((state: RootState) => state.Layout);
  const { layoutModeType } = layoutState;
  const isDarkMode = layoutModeType === 'dark';
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const isMobile = width <= 768;

  const [globalFilter, setGlobalFilter] = useState('');

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({
      itemRank,
    });
    return itemRank.passed;
  };

  const table = useReactTable({
    columns: columns,
    data,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      globalFilter,
    },
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const { getHeaderGroups, getRowModel, setPageSize, getState } = table;

  useEffect(() => {
    if (Number(customPageSize)) {
      setPageSize(Number(customPageSize));
    }
  }, [customPageSize, setPageSize]);

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      handlePageChange(currentPage - 1);
    }
    setInputPageValue(currentPage);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      handlePageChange(currentPage + 1);
      setInputPageValue(currentPage + 2);
    }
  };

  return (
    <Fragment>
      {isTagQuery && initialFilters && handleSearchFiltersSubmit && (
        <Row className="mb-3">
          <CardBody className="border border-dashed border-end-0 border-start-0">
            <TagSearchForm
              _isDarkMode={isDarkMode}
              onSubmit={handleSearchFiltersSubmit}
            />
            {selectedCheckBoxDelete && selectedCheckBoxDelete > 0 ? (
              <TextSelectedItems selectedItems={selectedCheckBoxDelete} />
            ) : null}
          </CardBody>
        </Row>
      )}

      {!isMobile && (
        <EntriesPagination
          customPageSize={customPageSize}
          setEntriesPerPage={setEntriesPerPage}
          currentPage={currentPage}
          totalPages={totalPages}
          handlePageChange={handlePageChange}
          handlePreviousPage={handlePreviousPage}
          handleNextPage={handleNextPage}
          inputPageValue={inputPageValue}
          setInputPageValue={setInputPageValue}
        />
      )}

      <div className={divClass}>
        <Table
          hover
          className={tableClass}
          style={{
            width: '100%',
          }}
        >
          <thead className={theadClass}>
            {getHeaderGroups().map((headerGroup: any) => (
              <tr className={trClass} key={headerGroup.id}>
                {headerGroup.headers.map((header: any) => (
                  <th
                    key={header.id}
                    className={thClass}
                    style={{
                      ...header.column.columnDef.meta?.style,
                    }}
                    {...{
                      onClick: header.column.getToggleSortingHandler(),
                    }}
                  >
                    {header.isPlaceholder ? null : (
                      <React.Fragment>
                        <div className={thClass}>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </div>
                        {header.column.getIsSorted() === 'asc'
                          ? ' '
                          : header.column.getIsSorted() === 'desc'
                          ? ' '
                          : null}
                        {header.column.getCanFilter() ? (
                          <div>
                            <Filter column={header.column} table={table} />
                          </div>
                        ) : null}
                      </React.Fragment>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={getHeaderGroups()[0].headers.length}>
                  <div className="text-muted text-center">
                    <span>{t('No results found')}</span>
                  </div>
                </td>
              </tr>
            ) : (
              getRowModel().rows.map((row: any) => {
                return (
                  <tr key={row.id} className={`row-${row.original.id}`}>
                    {row.getVisibleCells().map((cell: any) => {
                      return (
                        <td
                          key={cell.id}
                          style={{
                            ...cell.column.columnDef.meta?.style,
                          }}
                          className={`${tdClass}`}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      <ResultsPagination
        showingData={getState().pagination.pageSize}
        TotalData={TotalData}
        currentPage={currentPage}
        totalPages={totalPages}
        handlePageChange={handlePageChange}
        handlePreviousPage={handlePreviousPage}
        handleNextPage={handleNextPage}
        inputPageValue={inputPageValue}
        setInputPageValue={setInputPageValue}
      />
    </Fragment>
  );
};

export default TagTable;
